import React from "react";
import { format } from "date-fns";
import { Typography, Input, Menu, Button } from "@material-tailwind/react";
import TagInput from "./TagInput";
import { DatepickerInput } from "./DatapickerInput";
import { getMoneyFormat, getPercentFormat, getTextFormat } from "../utils/formatViews";

export interface Field {
    n: string;
    f: string;
    type: string;
    placeholder?: string;
    input_type?: string;
    editable: boolean;
    options?: { n: string; f: string; v: string; type: string }[];
    type_field?: string;
    cardClass?: string;
    textClass?: string;
    show?: string;
    hide?: string;
}

export const getField = (field: string, object: Record<string, any>) => {
    if (object?.id) {
        if (field.indexOf(".") !== -1) {
            const [key, subkey] = field.split(".");
            return object[key]?.[subkey] || "";
        } else {
            return object[field] || "";
        }
    }
};

export const getSelectField = (field: Field, object: Record<string, any>) => {
    const value = getField(field.f, object);
    const type = field.options?.find((el: any) => el.v === value);
    return type?.n || "N/A";
};

export const getCombinedField = (field: Field, object: Record<string, any>) => {
    const type = field.options?.find((el) => el.v === getField(field?.type_field || '', object));

    if (type?.type == 'money') {
        return getMoneyFormat(getField(field.f, object) as string);
    } else if (type?.type == 'percent') {
        return getPercentFormat(getField(field.f, object) as string);
    }

    return getTextFormat(getField(field.f, object) as string);
};


const handleChange = (field: string, value: string, type: string, setNewVehicle: (vehicle: Record<string, any>) => void) => {
    if (field.indexOf(".") !== -1) {
        const [key, subkey] = field.split(".");
        setNewVehicle((prev: Record<string, any>) => ({
            ...prev,
            [key]: {
                ...prev[key],
                [subkey]:
                    type === "money" || type === "number" || type === "percent"
                        ? Number(value)
                        : value,
            },
        }));
    } else {
        setNewVehicle((prev: Record<string, any>) => ({
            ...prev,
            [field]:
                type === "money" || type === "number" || type === "percent"
                    ? Number(value)
                    : value,
        }));
    }
};


export const renderField = (
    field: Field,
    isEdit: boolean,
    object: Record<string, any>,
    setNewVehicle: (vehicle: Record<string, any>) => void
) => {
    return (
        <React.Fragment>
            <Typography type="small" className="block text-foreground">
                {field.n}
            </Typography>
            {isEdit && field.editable ? (
                field.type === "select" ? (
                    <TagInput
                        id={field.f}
                        value={getField(field.f, object) as string}
                        setValue={(value: string) => {
                            handleChange(field.f, value, "string", setNewVehicle);
                        }}
                        label=""
                        wrapperClass="mt-1 w-[95%]"
                        placeholder={field.placeholder || "Select an option"}
                        options={
                            field.options?.map((option) => ({
                                value: option.v,
                                label: option.n,
                            })) || []
                        }
                    />
                ) : field.type === "date" ? (
                    <DatepickerInput
                        wrapperClass="mt-1 w-[95%]"
                        date={
                            object[field.f]
                                ? new Date(object[field.f] + 'T00:00:00')
                                : null
                        }
                        setDate={(date: Date) => {
                            handleChange(
                                field.f,
                                format(date, "yyyy-MM-dd"),
                                "date",
                                setNewVehicle
                            );
                        }}
                    />
                ) : field.type === "combined_input" ? (
                    <div className="relative flex w-full max-w-[24rem]">
                        <Menu placement="bottom-start">
                            <Menu.Trigger
                                as={Button}
                                color="secondary"
                                className="flex items-center gap-2 rounded-r-none border border-r-0 border-surface bg-secondary pl-3"
                            >
                                {getField(field?.type_field || "", object) as string}
                            </Menu.Trigger>
                            <Menu.Content className="overflow-y-auto">
                                {field.options?.map((el, index) => {
                                    return (
                                        <Menu.Item
                                            key={index}
                                            value={el.v}
                                            className="flex items-center gap-2"
                                            onClick={() =>
                                                handleChange(
                                                    field?.type_field || "",
                                                    el.v,
                                                    "string",
                                                    setNewVehicle
                                                )
                                            }
                                        >
                                            {el.n}
                                        </Menu.Item>
                                    );
                                })}
                            </Menu.Content>
                        </Menu>
                        <Input
                            placeholder={field.n}
                            type={field.input_type}
                            className="rounded-l-none"
                            value={getField(field.f, object) as string}
                            onChange={(e: React.ChangeEvent<HTMLInputElement>) =>
                                handleChange(
                                    field.f,
                                    e.target.value,
                                    field.input_type || "",
                                    setNewVehicle
                                )
                            }
                        />
                    </div>
                ) : (
                    <Input
                        type={field.type === "money" ? "number" : field.type}
                        className="mt-1 w-[95%]"
                        value={getField(field.f, object) as string}
                        onChange={(e: React.ChangeEvent<HTMLInputElement>) =>
                            handleChange(field.f, e.target.value, field.type, setNewVehicle)
                        }
                    />
                )
            ) : (
                <Typography className="block text-primary md:text-lg font-bold overflow-hidden text-ellipsis">
                    {field.type === "text" ? (
                        getTextFormat(getField(field.f, object) as string)
                    ) : field.type === "image" ? (
                        (getField(field.f, object) as string) ? (
                            <img
                                src={getField(field.f, object) as string}
                                alt="signature"
                                className="w-auto h-full max-h-16 object-contain"
                            />
                        ) : (
                            "N/A"
                        )

                    ) : field.type === "select" ? (
                        getSelectField(field, object)
                    ) : field.type === "money" ? (
                        getMoneyFormat(getField(field.f, object) as string)
                    ) : field.type === "combined_input" ? (
                        getCombinedField(field, object)
                    ) : (
                        getField(field.f, object) || "N/A"
                    )}
                </Typography>
            )}
        </React.Fragment>
    );
};
